import { useNavigate } from "react-router-dom";
import { useTimer } from "@/context/TimerContext";
import { formatClock } from "@/lib/constants";
import { Square, Timer as TimerIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function FloatingTimer() {
  const timer = useTimer();
  const navigate = useNavigate();
  if (!timer || !timer.active) return null;
  const { active, elapsed } = timer;

  const stop = async (e) => {
    e.stopPropagation();
    try {
      await timer.stop();
      toast.success("Timer stopped — time saved");
    } catch {
      toast.error("Could not stop the timer");
    }
  };

  return (
    <div
      data-testid="floating-timer"
      onClick={() => navigate(`/orders/${active.order_id}`)}
      className="group fixed bottom-5 right-5 z-[60] flex cursor-pointer items-center gap-3 rounded-full border border-emerald-500/40 bg-card/95 px-4 py-2.5 shadow-xl shadow-black/30 backdrop-blur transition-colors hover:border-emerald-500/70"
      title="Open this order"
    >
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
      </span>
      <TimerIcon className="h-4 w-4 shrink-0 text-emerald-500" />
      <div className="min-w-0 leading-tight">
        <div className="flex items-center gap-1.5 text-sm font-semibold">
          <span data-testid="floating-timer-clock" className="font-mono tabular-nums">{formatClock(elapsed)}</span>
        </div>
        <div className="max-w-[190px] truncate text-[11px] text-muted-foreground">
          {active.title || active.type}
          {active.address ? ` · ${active.address}` : ""}
        </div>
      </div>
      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      <button
        data-testid="floating-timer-stop"
        onClick={stop}
        className="ml-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-rose-500/50 bg-rose-500/15 text-rose-500 transition-colors hover:bg-rose-500/25"
        title="Stop timer"
      >
        <Square className="h-3.5 w-3.5 fill-current" />
      </button>
    </div>
  );
}
